import React, {
  useState,
  useContext,
  type PropsWithChildren,
  type Dispatch,
  type SetStateAction,
  useMemo,
} from 'react'

type WebinarAppStateContextValue = {
  error: Error | null
  setError: Dispatch<SetStateAction<Error | null>>
  scope: string | null
  setScope: Dispatch<SetStateAction<string | null>>
  publicChannelURL: string | null
  setPublicChannelURL: Dispatch<SetStateAction<string | null>>
  privateChannelURL: string | null
  setPrivateChannelURL: Dispatch<SetStateAction<string | null>>
}
type WebinarAppStateProviderProps = PropsWithChildren<{}>

const WebinarAppStateContext =
  React.createContext<WebinarAppStateContextValue | null>(null)

export const WebinarAppStateProvider = ({
  children,
}: WebinarAppStateProviderProps) => {
  const [error, setError] = useState<Error | null>(null)
  const [scope, setScope] = useState<string | null>(null)
  const [publicChannelURL, setPublicChannelURL] = useState<string | null>(null)
  const [privateChannelURL, setPrivateChannelURL] = useState<string | null>(
    null
  )

  const context = useMemo(
    () => ({
      error,
      setError,
      scope,
      setScope,
      publicChannelURL,
      setPublicChannelURL,
      privateChannelURL,
      setPrivateChannelURL,
    }),
    [error, privateChannelURL, publicChannelURL, scope]
  )

  return (
    <WebinarAppStateContext.Provider value={context}>
      {children}
    </WebinarAppStateContext.Provider>
  )
}

const useWebinarAppState = () => {
  const context = useContext(WebinarAppStateContext)

  if (context === null) {
    throw new Error(
      'useWebinarAppState must be used within an WebinarAppStateProvider'
    )
  }

  return context
}

export default useWebinarAppState
